"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  Bot,
  BriefcaseBusiness,
  Check,
  ChevronLeft,
  CircleAlert,
  ClipboardCopy,
  Download,
  History,
  LoaderCircle,
  Maximize2,
  Menu,
  MessageCircle,
  Plus,
  Send,
  Sparkles,
  SquarePen,
  X,
  Ban,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRunyiBackTrail } from "@/lib/use-runyi-back-trail";
import { Textarea } from "@/components/ui/textarea";
import {
  downloadFileFromR2,
  uploadFileToR2,
  type R2Transfer,
} from "@/lib/runyi-r2-client";
import type {
  ActivityEvent,
  Attachment,
  Conversation,
  InvokeResult,
  Message,
  WorkTask,
  WorkspaceContext,
  WorkspaceMode,
} from "./ai-workspace-types";
import {
  CenteredState,
  LiveProcessStatus,
  MessageBubble,
  WorkTaskCard,
  WorkspaceHistory,
} from "./ai-workspace-components";

const suggestions: Record<WorkspaceMode, string[]> = {
  chat: [
    "Develop a fresh movie premise from my theme",
    "Audit this story for originality and filler",
    "Turn my idea into a scene-by-scene outline",
  ],
  work: [
    "Inspect the RUNYI GitHub repository and summarize the current architecture",
    "Create a feature branch, make a safe code change, and open a pull request",
    "Open a website in the remote browser, inspect it, and report what you find",
  ],
};

function titleFromMessage(message: string) {
  const clean = message.replace(/\s+/g, " ").trim();
  if (!clean) return "New conversation";
  return clean.length > 52 ? `${clean.slice(0, 49)}…` : clean;
}

function attachmentMimeType(file: File, safeName: string) {
  if (file.type) return file.type;
  const extension = safeName.toLowerCase().split(".").pop() || "";
  const known: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    gif: "image/gif",
    svg: "image/svg+xml",
    heic: "image/heic",
    avif: "image/avif",
    mp4: "video/mp4",
    mov: "video/quicktime",
    webm: "video/webm",
    mkv: "video/x-matroska",
    m4v: "video/x-m4v",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    m4a: "audio/mp4",
    aac: "audio/aac",
    ogg: "audio/ogg",
    txt: "text/plain",
    pdf: "application/pdf",
    json: "application/json",
    zip: "application/zip",
  };
  return known[extension] || "application/octet-stream";
}

function isMediaMimeType(mimeType: string) {
  return /^(image|video|audio)\//i.test(mimeType);
}

const DOCUMENT_ATTACHMENT_LIMIT = 25 * 1024 * 1024;
const MEDIA_ATTACHMENT_LIMIT = 2 * 1024 * 1024 * 1024;

export function AiWorkspace({
  supabase,
  connected,
  isAdmin,
  mode,
  setMode,
  aiSelection,
  setAiSelection,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
  mode: WorkspaceMode;
  setMode: React.Dispatch<React.SetStateAction<WorkspaceMode>>;
  aiSelection: { provider_key: string; model_key: string };
  setAiSelection: React.Dispatch<React.SetStateAction<{ provider_key: string; model_key: string }>>;
}) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [thinkingStartedAt, setThinkingStartedAt] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState<number | null>(null);
  const [attachmentUploadName, setAttachmentUploadName] = useState<string | null>(null);
  const [fileMenuOpen, setFileMenuOpen] = useState(false);
  const [canWriteTxtFiles, setCanWriteTxtFiles] = useState(false);
  const [workspaceContext, setWorkspaceContext] = useState<WorkspaceContext>({});
  const [activeProcessRequestId, setActiveProcessRequestId] = useState<string | null>(null);
  const [processEvents, setProcessEvents] = useState<ActivityEvent[]>([]);
  const [backgroundWork, setBackgroundWork] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [workTasks, setWorkTasks] = useState<WorkTask[]>([]);
  const [activeWorkTaskId, setActiveWorkTaskId] = useState<string | null>(null);
  const [taskActionId, setTaskActionId] = useState<string | null>(null);
  const [closingBrowserSessionId, setClosingBrowserSessionId] = useState<string | null>(null);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const autoScrollToBottomRef = useRef(true);
  const syncedCompletedTaskIdsRef = useRef<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentUploadAbortRef = useRef<AbortController | null>(null);

  const preservingConversationRef = useRef(false);

  useEffect(() => {
    const onBack = (event: Event) => {
      if (event.defaultPrevented) return;
      if (fileMenuOpen) { event.preventDefault(); setFileMenuOpen(false); return; }
      if (mobileHistoryOpen) { event.preventDefault(); setMobileHistoryOpen(false); }
    };
    window.addEventListener("runyi:request-back", onBack);
    return () => window.removeEventListener("runyi:request-back", onBack);
  }, [fileMenuOpen, mobileHistoryOpen]);

  useRunyiBackTrail({ mode, activeConversationId }, (previous) => {
    if (previous.mode !== mode) preservingConversationRef.current = true;
    setMode(previous.mode);
    setActiveConversationId(previous.activeConversationId);
    setMobileHistoryOpen(false);
    if (previous.mode !== mode) setMessages([]);
  });


  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === activeConversationId) ?? null,
    [activeConversationId, conversations],
  );

  const loadTxtCapabilities = useCallback(async () => {
    if (!supabase || !connected) {
      setCanWriteTxtFiles(false);
      setWorkspaceContext({});
      return;
    }

    const [
      { data: account, error: accountError },
      { data: project },
    ] = await Promise.all([
      supabase.rpc("runyi_my_account"),
      supabase.rpc("runyi_member_project_profile"),
    ]);

    if (accountError) {
      setCanWriteTxtFiles(false);
      setWorkspaceContext({});
      return;
    }

    setCanWriteTxtFiles(account?.can_write_txt_files === true);
    setWorkspaceContext({
      email: account?.email ?? null,
      role: account?.role ?? null,
      project_ref: project?.configured === true ? project?.project_ref ?? null : null,
      project_status: project?.configured === true ? project?.connection_status ?? null : null,
      m4_profile: account?.m4_access?.profile_key ?? null,
    });
  }, [connected, supabase]);

  const loadProcessEvents = useCallback(async (requestId: string) => {
    if (!supabase || !connected || !requestId) return;

    const { data, error } = await supabase
      .from("ai_activity_events")
      .select("id,request_id,conversation_id,task_id,mode,sequence,event_key,label,status,tool_name,detail,created_at,completed_at")
      .eq("request_id", requestId)
      .order("sequence", { ascending: true })
      .limit(80);

    if (error) return;

    const next = (data ?? []) as ActivityEvent[];
    setProcessEvents(next);

    const terminalKeys = new Set([
      "completed",
      "response_failed",
      "context_failed",
      "plugins_failed",
      "conversation_failed",
    ]);
    const terminal = next.find((event) => terminalKeys.has(event.event_key));
    if (terminal) {
      setActiveProcessRequestId((current) => current === requestId ? null : current);
    }
  }, [connected, supabase]);

  const loadConversations = useCallback(async () => {
    if (!supabase || !connected) {
      setConversations([]);
      return;
    }
    setLoadingConversations(true);
    const { data, error } = await supabase
      .from("ai_conversations")
      .select("id,title,mode,updated_at")
      .eq("mode", mode)
      .eq("conversation_namespace", "RUNYI_AI")
      .order("updated_at", { ascending: false })
      .limit(40);
    setLoadingConversations(false);
    if (error) {
      const missingSchema = error.code === "42P01" || /ai_conversations|schema cache/i.test(error.message);
      setNotice(
        missingSchema
          ? "The AI workspace database upgrade is not installed yet."
          : error.message,
      );
      return;
    }
    setConversations((data ?? []) as Conversation[]);
    setNotice(null);
  }, [connected, mode, supabase]);

  const loadWorkTasks = useCallback(async () => {
    if (!supabase || !connected || mode !== "work") {
      setWorkTasks([]);
      return;
    }

    const { data, error } = await supabase
      .from("ai_work_tasks")
      .select("id,conversation_id,prompt,status,attempt_count,max_attempts,response_id,output_text,error,created_at,updated_at")
      .order("created_at", { ascending: false })
      .limit(20);

    if (error) {
      setNotice(error.message);
      return;
    }

    const next = (data ?? []) as WorkTask[];
    setWorkTasks(next);

    if (activeWorkTaskId) {
      const active = next.find((task) => task.id === activeWorkTaskId);
      if (active?.status === "completed" && active.conversation_id) {
        setActiveConversationId(active.conversation_id);
        setActiveWorkTaskId(null);
        await loadConversations();
      } else if (active?.status === "failed" || active?.status === "cancelled") {
        setActiveWorkTaskId(null);
      }
    }
  }, [activeWorkTaskId, connected, loadConversations, mode, supabase]);

  const loadMessages = useCallback(async (conversationId: string) => {
    if (!supabase || !connected) return;
    setLoadingMessages(true);
    const { data, error } = await supabase
      .from("ai_messages")
      .select("id,role,content,created_at,attachments")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    setLoadingMessages(false);
    if (error) {
      setNotice(error.message);
      return;
    }
    setMessages((data ?? []) as Message[]);
    setNotice(null);
  }, [connected, supabase]);

  useEffect(() => {
    if (preservingConversationRef.current) {
      preservingConversationRef.current = false;
      void loadConversations();
      return; // Back/openConversation restored a specific conversation.
    }
    setActiveConversationId(null);
    setMessages([]);
    setDraft("");
    setAttachments([]);
    setFileMenuOpen(false);
    setNotice(null);
    setActiveProcessRequestId(null);
    setProcessEvents([]);
    void loadConversations();
  }, [loadConversations, mode]);

  useEffect(() => {
    void loadTxtCapabilities();
  }, [loadTxtCapabilities]);

  useEffect(() => {
    if (!activeProcessRequestId) return;
    void loadProcessEvents(activeProcessRequestId);
    const timer = window.setInterval(() => {
      void loadProcessEvents(activeProcessRequestId);
    }, 650);
    return () => window.clearInterval(timer);
  }, [activeProcessRequestId, loadProcessEvents]);

  useEffect(() => {
    if (!activeConversationId) return;
    void loadMessages(activeConversationId);
  }, [activeConversationId, loadMessages]);

  useEffect(() => {
    void loadWorkTasks();
    if (mode !== "work") return;
    const timer = window.setInterval(() => void loadWorkTasks(), 2500);
    return () => window.clearInterval(timer);
  }, [loadWorkTasks, mode]);

  useEffect(() => {
    if (!activeConversationId || mode !== "work") return;
    const task = workTasks.find(
      (item) => item.conversation_id === activeConversationId && item.status === "completed",
    );
    if (!task || syncedCompletedTaskIdsRef.current.has(task.id)) return;
    syncedCompletedTaskIdsRef.current.add(task.id);
    void loadMessages(activeConversationId);
  }, [activeConversationId, loadMessages, mode, workTasks]);

  useEffect(() => {
    const area = scrollAreaRef.current;
    if (!area || !autoScrollToBottomRef.current) return;
    area.scrollTo({
      top: area.scrollHeight,
      behavior: "auto",
    });
  }, [messages, sending]);

  function handleWorkspaceScroll() {
    const area = scrollAreaRef.current;
    if (!area) return;
    const distanceFromBottom = area.scrollHeight - area.scrollTop - area.clientHeight;
    autoScrollToBottomRef.current = distanceFromBottom <= 24;
  }

  function lockWorkspaceScrollForReading() {
    autoScrollToBottomRef.current = false;
  }

  function startNewConversation() {
    autoScrollToBottomRef.current = true;
    syncedCompletedTaskIdsRef.current.clear();
    setActiveConversationId(null);
    setMessages([]);
    setDraft("");
    setAttachments([]);
    setNotice(null);
    setActiveWorkTaskId(null);
    setActiveProcessRequestId(null);
    setProcessEvents([]);
    setMobileHistoryOpen(false);
  }

  function openConversation(conversation: Conversation) {
    autoScrollToBottomRef.current = true;
    syncedCompletedTaskIdsRef.current.clear();
    if (conversation.mode !== mode) preservingConversationRef.current = true;
    setMode(conversation.mode);
    setActiveConversationId(conversation.id);
    setMobileHistoryOpen(false);
  }

  async function deleteConversation(conversation: Conversation) {
    if (!supabase || !isAdmin) return;
    if (!window.confirm(`Delete “${conversation.title}” and its complete message history? This cannot be undone.`)) return;
    setNotice(null);
    const { data, error } = await supabase.functions.invoke("runyi-ai", {
      body: {
        action: "delete_conversation",
        conversation_id: conversation.id,
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    if (errorMessage) { setNotice(errorMessage); return; }
    if (activeConversationId === conversation.id) startNewConversation();
    await loadConversations();
  }

  async function addAttachments(files: FileList | null) {
    if (!files?.length || !supabase || !connected) return;
    if (backgroundWork) {
      setNotice("Switch Work to Live mode before attaching files.");
      return;
    }

    setFileMenuOpen(false);
    setUploadingAttachment(true);
    setNotice(null);

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) throw new Error("Your RUNYI session is not available.");

      const remaining = Math.max(0, 4 - attachments.length);
      const selected = Array.from(files).slice(0, remaining);
      if (!selected.length) {
        setNotice("RUNYI supports up to 4 attached files per message.");
        return;
      }

      const uploaded: Attachment[] = [];
      const uploadController = new AbortController();
      attachmentUploadAbortRef.current = uploadController;

      for (const file of selected) {
        const safeName = file.name
          .replace(/[\\/\0]/g, "_")
          .replace(/[^A-Za-z0-9._ -]+/g, "_")
          .trim()
          .slice(0, 160) || "file";
        const isTxt = safeName.toLowerCase().endsWith(".txt") || file.type === "text/plain";
        const folder = isTxt ? "saved-txt" : "uploads";
        const mimeType = isTxt ? "text/plain" : attachmentMimeType(file, safeName);
        const isMedia = isMediaMimeType(mimeType);
        const sizeLimit = isMedia ? MEDIA_ATTACHMENT_LIMIT : DOCUMENT_ATTACHMENT_LIMIT;

        if (file.size > sizeLimit) {
          throw new Error(
            isMedia
              ? `${file.name} is larger than the 2 GB RUNYI media attachment limit.`
              : `${file.name} is larger than the 25 MB RUNYI document attachment limit.`,
          );
        }

        if (isTxt && !canWriteTxtFiles) {
          throw new Error("TXT file save access has not been granted to this account.");
        }

        if (isMedia) {
          const { data: intent, error: intentError } = await supabase.functions.invoke<{
            object?: { object_id?: string };
            location?: {
              provider_key?: string;
              bucket_ref?: string;
              object_key?: string;
            };
            transfer?: R2Transfer;
            error?: string;
          }>("runyi-storage-broker", {
            body: {
              operation: "upload_intent",
              tenant_user_id: userData.user.id,
              filename: safeName,
              mime_type: mimeType,
              size_bytes: file.size,
              storage_class: "MEDIA_WORKING",
              workspace_ref: activeConversationId || "ai-workspace",
              expires_in: 7200,
            },
          });

          if (
            intentError ||
            !intent?.object?.object_id ||
            intent?.location?.provider_key !== "CLOUDFLARE" ||
            !intent?.location?.object_key ||
            intent?.transfer?.transport !== "S3_TEMPORARY_CREDENTIALS"
          ) {
            throw new Error(
              intent?.error ||
              intentError?.message ||
              "RUNYI could not create the Cloudflare media upload grant.",
            );
          }

          setAttachmentUploadName(safeName);
          setAttachmentUploadProgress(0);
          await uploadFileToR2(intent.transfer, file, mimeType, {
            signal: uploadController.signal,
            onProgress: (percent) => setAttachmentUploadProgress(percent),
          });

          const { data: confirmation, error: confirmError } =
            await supabase.functions.invoke<{
              status?: string;
              error?: string;
            }>("runyi-storage-broker", {
              body: {
                operation: "confirm_upload",
                object_id: intent.object.object_id,
              },
            });

          if (confirmError || confirmation?.status !== "AVAILABLE") {
            throw new Error(
              confirmation?.error ||
              confirmError?.message ||
              "RUNYI could not verify the Cloudflare media upload.",
            );
          }

          uploaded.push({
            name: safeName,
            mime_type: mimeType,
            size: file.size,
            storage_path: intent.location.object_key,
            storage_provider: "CLOUDFLARE",
            storage_object_id: intent.object.object_id,
          });
        } else {
          const storagePath =
            `${userData.user.id}/${folder}/${Date.now()}-${crypto.randomUUID()}-${safeName}`;

          const { error: uploadError } = await supabase.storage
            .from("runyi-browser-files")
            .upload(storagePath, file, {
              contentType: mimeType,
              upsert: false,
            });
          if (uploadError) throw new Error(uploadError.message);

          uploaded.push({
            name: safeName,
            mime_type: mimeType,
            size: file.size,
            storage_path: storagePath,
            storage_provider: "SUPABASE",
          });
        }
      }

      setAttachments((current) => [...current, ...uploaded].slice(0, 4));
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setNotice("RUNYI media upload cancelled.");
      } else {
        setNotice(error instanceof Error ? error.message : String(error));
      }
    } finally {
      attachmentUploadAbortRef.current = null;
      setAttachmentUploadProgress(null);
      setAttachmentUploadName(null);
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function downloadWorkspaceFile(file: Attachment) {
    if (!supabase || !connected) {
      setNotice("Sign in before downloading RUNYI workspace files.");
      return;
    }

    setNotice(null);

    if (file.storage_provider === "CLOUDFLARE" && file.storage_object_id) {
      const { data, error } = await supabase.functions.invoke<{
        transfer?: R2Transfer;
        error?: string;
      }>("runyi-storage-broker", {
        body: {
          operation: "download_intent",
          object_id: file.storage_object_id,
          expires_in: 300,
        },
      });

      if (
        error ||
        data?.transfer?.transport !== "S3_TEMPORARY_CREDENTIALS"
      ) {
        setNotice(
          data?.error ||
          error?.message ||
          "RUNYI could not create a secure Cloudflare download grant.",
        );
        return;
      }

      try {
        const blob = await downloadFileFromR2(data.transfer);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = file.name;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (downloadError) {
        setNotice(
          downloadError instanceof Error
            ? downloadError.message
            : "RUNYI could not download this Cloudflare file.",
        );
      }
      return;
    }

    const { data, error } = await supabase.storage
      .from("runyi-browser-files")
      .createSignedUrl(file.storage_path, 300, { download: true });
    if (error || !data?.signedUrl) {
      setNotice(error?.message || "RUNYI could not create a secure download link for this file.");
      return;
    }

    const link = document.createElement("a");
    link.href = data.signedUrl;
    link.download = file.name;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  async function sendMessage(text?: string) {
    const message = (text ?? draft).trim();
    if ((!message && attachments.length === 0) || sending || uploadingAttachment) return;
    const outgoingAttachments = attachments.slice(0, 4);
    if (!supabase || !connected) {
      setNotice("Sign in and connect the RUNYI database before using the AI workspace.");
      return;
    }

    autoScrollToBottomRef.current = true;

    const optimisticUser: Message = {
      id: `local-${Date.now()}`,
      role: "user",
      content: message || "Attached file",
      attachments: outgoingAttachments,
      created_at: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimisticUser]);
    setDraft("");
    setAttachments([]);
    const requestStartedAt = Date.now();
    const processRequestId = crypto.randomUUID();
    setThinkingStartedAt(requestStartedAt);
    setActiveProcessRequestId(processRequestId);
    setProcessEvents([]);
    setSending(true);
    setNotice(null);

    if (mode === "work" && backgroundWork) {
      if (outgoingAttachments.length > 0) {
        setSending(false);
        setThinkingStartedAt(null);
        setAttachments(outgoingAttachments);
        setNotice("Attached browser files require Live mode. Switch from Background to Live and send again.");
        return;
      }
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        task_id?: string;
        status?: string;
        conversation_id?: string | null;
        background?: boolean;
        error?: string;
      }>("runyi-work-task", {
        body: {
          action: "queue",
          message,
          conversation_id: activeConversationId,
          provider_key: aiSelection.provider_key || undefined,
          model_key: aiSelection.model_key || undefined,
        },
      });

      setSending(false);
      setThinkingStartedAt(null);

      if (error || !data?.task_id) {
        let detail = data?.error || error?.message || "RUNYI Work could not queue the background task.";
        const context = (error as { context?: Response } | null)?.context;
        if (context) {
          try {
            const payload = await context.clone().json() as { error?: string };
            if (payload?.error) detail = payload.error;
          } catch {
            // Keep the best available error message.
          }
        }
        setNotice(detail);
        return;
      }

      setActiveWorkTaskId(data.task_id);
      setActiveProcessRequestId(data.task_id);
      setProcessEvents([]);
      setNotice("RUNYI Work is running this task in the background. You can leave the app and return later.");
      await loadWorkTasks();
      return;
    }

    const { data, error } = await supabase.functions.invoke<InvokeResult>("runyi-ai", {
      body: {
        mode,
        message,
        attachments: outgoingAttachments,
        conversation_id: activeConversationId,
        provider_key: aiSelection.provider_key || undefined,
        model_key: aiSelection.model_key || undefined,
        request_id: processRequestId,
      },
    });

    if (error || !data?.output_text) {
      setSending(false);
      setThinkingStartedAt(null);
      let detail =
        error?.message ||
        "The selected AI model could not be reached. Verify the enterprise provider adapter, authorization and model availability.";

      const context = (error as { context?: Response } | null)?.context;
      if (context) {
        try {
          const payload = await context.clone().json() as {
            error?: string;
            type?: string | null;
            code?: string | null;
          };
          if (payload?.error) {
            detail = payload.error;
            const suffix = [payload.type, payload.code].filter(Boolean).join(" · ");
            if (suffix) detail += ` (${suffix})`;
          }
        } catch {
          try {
            const raw = await context.clone().text();
            if (raw.trim()) detail = raw.trim();
          } catch {
            // Keep the original Supabase error message.
          }
        }
      }

      setNotice(detail);
      setAttachments(outgoingAttachments);
      return;
    }

    const closedSessions = new Set(data.closed_browser_sessions ?? []);
    const assistantMessage: Message = {
      id: data.response_id || `assistant-${Date.now()}`,
      role: "assistant",
      content: data.output_text,
      created_at: new Date().toISOString(),
      attachments: data.workspace_files?.slice(0, 4) ?? [],
      thinking_seconds: Math.max(1, Math.round((Date.now() - requestStartedAt) / 1000)),
      visual_previews: data.visual_previews?.slice(0, 4).map((preview) => ({
        ...preview,
        session_closed: Boolean(preview.session_id && closedSessions.has(preview.session_id)),
      })),
    };
    setMessages((current) => [
      ...current.map((item) => {
        if (!item.visual_previews?.length || closedSessions.size === 0) return item;
        return {
          ...item,
          visual_previews: item.visual_previews.filter(
            (preview) => !preview.session_id || !closedSessions.has(preview.session_id),
          ),
        };
      }),
      assistantMessage,
    ]);
    setActiveConversationId(data.conversation_id);
    setSending(false);
    setThinkingStartedAt(null);
    await loadProcessEvents(data.request_id || processRequestId);

    setConversations((current) => {
      const title = activeConversation?.title || titleFromMessage(message);
      const next: Conversation = {
        id: data.conversation_id,
        title,
        mode,
        updated_at: new Date().toISOString(),
      };
      return [next, ...current.filter((item) => item.id !== next.id)];
    });

    window.setTimeout(() => {
      void loadConversations();
    }, 250);
  }

  function clearBrowserPreview(previewId: string) {
    setMessages((current) =>
      current.map((message) => ({
        ...message,
        visual_previews: message.visual_previews?.filter((preview) => preview.id !== previewId),
      })),
    );
  }

  async function closeBrowserSession(sessionId: string) {
    if (!supabase || !sessionId || closingBrowserSessionId) return;

    setClosingBrowserSessionId(sessionId);
    setNotice(null);

    const { data, error } = await supabase.functions.invoke<{
      ok?: boolean;
      closed?: boolean;
      session_id?: string;
      error?: string;
    }>("runyi-browser", {
      body: {
        action: "close",
        session_id: sessionId,
      },
    });

    setClosingBrowserSessionId(null);

    if (error || data?.error || data?.closed !== true) {
      let detail = data?.error || error?.message || "The remote browser session could not be closed.";
      const context = (error as { context?: Response } | null)?.context;
      if (context) {
        try {
          const payload = await context.clone().json() as { error?: string };
          if (payload?.error) detail = payload.error;
        } catch {
          // Keep the best available error message.
        }
      }
      setNotice(detail);
      return;
    }

    setMessages((current) =>
      current.map((message) => ({
        ...message,
        visual_previews: message.visual_previews?.filter(
          (preview) => preview.session_id !== sessionId,
        ),
      })),
    );
    setNotice("Remote browser session closed. Temporary screenshots from that session were cleared.");
  }

  async function controlWorkTask(taskId: string, action: "retry" | "cancel") {
    if (!supabase || taskActionId) return;
    setTaskActionId(taskId);
    setNotice(null);

    const { data, error } = await supabase.functions.invoke<{
      ok?: boolean;
      task_id?: string;
      status?: string;
      error?: string;
    }>("runyi-work-task", {
      body: {
        action,
        task_id: taskId,
      },
    });

    setTaskActionId(null);

    if (error || data?.error) {
      let detail = data?.error || error?.message || `RUNYI Work could not ${action} this task.`;
      const context = (error as { context?: Response } | null)?.context;
      if (context) {
        try {
          const payload = await context.clone().json() as { error?: string };
          if (payload?.error) detail = payload.error;
        } catch {
          // Keep the best available error message.
        }
      }
      setNotice(detail);
      return;
    }

    if (action === "retry") {
      setActiveWorkTaskId(taskId);
      setNotice("RUNYI Work task resumed in the background.");
    } else {
      if (activeWorkTaskId === taskId) setActiveWorkTaskId(null);
      setNotice("RUNYI Work task cancelled.");
    }

    await loadWorkTasks();
  }

  function handleComposerKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  }

  return (
    <div className="relative mx-auto flex h-full min-h-0 w-full max-w-none overflow-hidden bg-background">
      <aside className="hidden w-56 shrink-0 bg-foreground/[0.018] lg:flex lg:flex-col">
        <WorkspaceHistory
          mode={mode}
          conversations={conversations}
          activeConversationId={activeConversationId}
          loading={loadingConversations}
          onNew={startNewConversation}
          onOpen={openConversation}
          isAdmin={isAdmin}
          onDelete={(conversation) => void deleteConversation(conversation)}
        />
      </aside>

      {mobileHistoryOpen && (
        <div className="absolute inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/35"
            aria-label="Close conversation history"
            onClick={() => setMobileHistoryOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-[82%] max-w-xs flex-col bg-background shadow-2xl">
            <div className="flex items-center justify-end p-3">
              <Button variant="ghost" size="icon" onClick={() => setMobileHistoryOpen(false)}>
                <X className="size-5" />
              </Button>
            </div>
            <WorkspaceHistory
              mode={mode}
              conversations={conversations}
              activeConversationId={activeConversationId}
              loading={loadingConversations}
              onNew={startNewConversation}
              onOpen={openConversation}
              isAdmin={isAdmin}
              onDelete={(conversation) => void deleteConversation(conversation)}
            />
          </aside>
        </div>
      )}

      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header className="relative z-20 flex shrink-0 flex-col bg-background/90 px-2 py-1 backdrop-blur-xl sm:px-4">
          <div className="relative flex min-h-12 items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileHistoryOpen(true)}
              className="absolute left-0 size-8 rounded-full lg:hidden sm:size-9"
              aria-label="Open conversation history"
            >
              <Menu className="size-5" />
            </Button>

            <div className="grid grid-cols-2 rounded-full bg-foreground/[0.065] p-0.5">
              <button
                type="button"
                onClick={() => setMode("chat")}
                className={`min-w-[68px] rounded-full px-3 py-1 text-[13px] font-medium transition sm:min-w-[76px] sm:px-4 sm:py-1.5 sm:text-[14px] ${
                  mode === "chat"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-foreground/48"
                }`}
              >
                Chat
              </button>
              <button
                type="button"
                onClick={() => setMode("work")}
                className={`min-w-[74px] rounded-full px-4 py-1.5 text-[14px] font-medium transition sm:min-w-[82px] sm:px-5 ${
                  mode === "work"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-foreground/48"
                }`}
              >
                Work
              </button>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={startNewConversation}
              className="absolute right-0 size-8 rounded-full sm:size-9"
              aria-label="New conversation"
            >
              <SquarePen className="size-5" />
            </Button>
          </div>
        </header>

        <div className="mx-auto flex w-full max-w-5xl shrink-0 items-center justify-between gap-3 px-5 pb-1 text-[10px] text-foreground/38 sm:px-10 sm:text-[11px] lg:px-14">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium text-foreground/58">{workspaceContext.email ?? "RUNYI account"}</span>
            <span className="text-foreground/20">·</span>
            <span className="truncate font-mono">{workspaceContext.project_ref ?? "Central RUNYI"}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {workspaceContext.m4_profile && <span className="hidden sm:inline">{workspaceContext.m4_profile}</span>}
            <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-500 dark:text-emerald-300">
              <span className="size-1.5 rounded-full bg-emerald-400" />
              {connected ? "LIVE" : "OFFLINE"}
            </span>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            ref={scrollAreaRef}
            onScroll={handleWorkspaceScroll}
            onTouchMove={lockWorkspaceScrollForReading}
            onWheel={lockWorkspaceScrollForReading}
            className="scrollbar-none min-h-0 flex-1 overscroll-contain overflow-y-auto px-5 pb-8 pt-7 touch-pan-y sm:px-10 sm:pb-10 sm:pt-10 lg:px-16 xl:px-20"
          >
            {!connected ? (
              <CenteredState
                icon={CircleAlert}
                title="Connect RUNYI first"
                body="The AI workspace uses your authenticated Supabase session. Open Settings, connect your account, then return here."
              />
            ) : loadingMessages ? (
              <div className="grid min-h-[48vh] place-items-center">
                <LoaderCircle className="size-6 animate-spin text-foreground/35" />
              </div>
            ) : messages.length === 0 && !(mode === "work" && workTasks.some((task) => ["queued", "running", "failed"].includes(task.status))) ? (
              <div className="mx-auto flex h-full min-h-[62vh] max-w-3xl flex-col justify-center py-10 sm:py-16">
                <div className="mb-12 text-center">
                  <div className="mx-auto grid size-11 place-items-center rounded-full bg-foreground/[0.035] text-foreground/70">
                    {mode === "chat" ? (
                      <MessageCircle className="size-5" />
                    ) : (
                      <BriefcaseBusiness className="size-5" />
                    )}
                  </div>
                  <h1 className="mt-4 text-2xl font-semibold tracking-tight">
                    {mode === "chat" ? "RUNYI Chat" : "RUNYI Work"}
                  </h1>
                  <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-foreground/45">
                    {mode === "chat"
                      ? "Your universal enterprise AI conversation, using authorized providers and selected models."
                      : "An agent-style workspace with background/resumable tasks, persistent browser session tracking, multi-tab remote browsing, GitHub, Cloudflare, and controlled Supabase tools. Protected operations are reviewed in the governed New Infrastructure or Enterprise Accounts workspace."}
                  </p>
                </div>

                <div className="space-y-2">
                  {suggestions[mode].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => void sendMessage(suggestion)}
                      className="flex w-full items-center gap-3 rounded-2xl bg-foreground/[0.022] px-4 py-3.5 text-left text-[15px] text-foreground/62 transition hover:bg-foreground/[0.05] hover:text-foreground"
                    >
                      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-foreground/[0.035]">
                        {mode === "chat" ? (
                          <Sparkles className="size-4" />
                        ) : (
                          <Bot className="size-4" />
                        )}
                      </span>
                      <span>{suggestion}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mx-auto w-full max-w-4xl space-y-9 py-3 sm:space-y-11 sm:py-6">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    closingBrowserSessionId={closingBrowserSessionId}
                    onCloseBrowserSession={(sessionId) => void closeBrowserSession(sessionId)}
                    onClearPreview={clearBrowserPreview}
                    onDownloadAttachment={(attachment) => void downloadWorkspaceFile(attachment)}
                  />
                ))}
                {mode === "work" && workTasks.some((task) => ["queued", "running", "failed"].includes(task.status)) && (
                  <div className="space-y-3">
                    {workTasks
                      .filter((task) => ["queued", "running", "failed"].includes(task.status))
                      .slice(0, 5)
                      .map((task) => (
                        <WorkTaskCard
                          key={task.id}
                          task={task}
                          busy={taskActionId === task.id}
                          onRetry={() => void controlWorkTask(task.id, "retry")}
                          onCancel={() => void controlWorkTask(task.id, "cancel")}
                        />
                      ))}
                  </div>
                )}
                {(sending || processEvents.length > 0 || activeProcessRequestId) && (
                  <LiveProcessStatus
                    mode={mode}
                    events={processEvents}
                    active={Boolean(sending || activeProcessRequestId)}
                    startedAt={thinkingStartedAt}
                  />
                )}
              </div>
            )}
          </div>

          <div className="z-20 shrink-0 bg-background/95 px-3 pb-3 pt-2 backdrop-blur-xl sm:px-8 sm:pb-5 sm:pt-3 lg:px-16">
            <div className="mx-auto max-w-3xl">
              {notice && (
                <div className="mb-2 rounded-2xl border border-amber-300/20 bg-amber-300/8 px-4 py-2.5 text-xs leading-5 text-amber-700 dark:text-amber-100">
                  {notice}
                </div>
              )}
              <div className="relative rounded-[26px] bg-foreground/[0.045] p-1.5 shadow-[0_10px_35px_rgba(0,0,0,0.08)] sm:rounded-[28px] sm:p-2">
                {uploadingAttachment && attachmentUploadProgress !== null && (
                  <div className="mx-2 mb-1 mt-1 rounded-xl border border-foreground/10 bg-background/65 px-3 py-2">
                    <div className="flex items-center gap-2 text-[10px] text-foreground/55">
                      <span className="min-w-0 flex-1 truncate">
                        Uploading {attachmentUploadName || "media"}
                      </span>
                      <span className="font-semibold text-foreground/70">
                        {attachmentUploadProgress}%
                      </span>
                      <button
                        type="button"
                        onClick={() => attachmentUploadAbortRef.current?.abort()}
                        className="rounded-full px-2 py-0.5 font-semibold text-foreground/65 hover:bg-foreground/[0.07]"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-foreground/10">
                      <div
                        className="h-full rounded-full bg-foreground transition-[width] duration-200"
                        style={{ width: `${attachmentUploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 px-2 pb-1 pt-1">
                    {attachments.map((attachment) => (
                      <span
                        key={attachment.storage_path}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-foreground/10 bg-foreground/[0.04] px-2.5 py-1 text-[10px] text-foreground/65"
                      >
                        <span className="max-w-[180px] truncate">{attachment.name}</span>
                        <button
                          type="button"
                          aria-label={`Remove ${attachment.name}`}
                          onClick={() => setAttachments((current) => current.filter((item) => item.storage_path !== attachment.storage_path))}
                          className="rounded-full p-0.5 hover:bg-foreground/10"
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {fileMenuOpen && (
                  <div className="absolute bottom-[58px] left-2 z-30 w-[min(22rem,calc(100vw-3rem))] overflow-hidden rounded-2xl bg-background p-2 shadow-2xl">
                    <button
                      type="button"
                      onClick={() => {
                        setFileMenuOpen(false);
                        fileInputRef.current?.click();
                      }}
                      className="w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium hover:bg-foreground/[0.06]"
                    >
                      Upload files from device
                      <span className="mt-0.5 block text-[11px] font-normal text-foreground/45">
                        Media uploads use Cloudflare R2 (up to 2 GB); documents stay on Supabase (up to 25 MB).
                      </span>
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => void addAttachments(event.target.files)}
                />
                <Textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  disabled={!connected || sending}
                  rows={1}
                  placeholder={mode === "chat" ? "Ask RUNYI" : "Give RUNYI a task"}
                  className="max-h-32 min-h-[38px] resize-none border-0 bg-transparent px-3 py-1.5 text-[16px] leading-6 shadow-none outline-none placeholder:text-foreground/32 focus-visible:ring-0 sm:min-h-[42px] sm:py-2"
                />
                <div className="flex items-center justify-between px-1 pb-0.5">
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFileMenuOpen((value) => !value)}
                      disabled={!connected || sending || uploadingAttachment || (mode === "work" && backgroundWork) || attachments.length >= 4}
                      className="size-8 rounded-full bg-transparent text-foreground/55 opacity-100 hover:bg-foreground/[0.06] sm:size-9"
                      title={mode === "work" && backgroundWork ? "Switch to Live mode to attach files" : "Upload files"}
                    >
                      {uploadingAttachment ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                    </Button>
                    {mode === "work" && (
                      <button
                        type="button"
                        onClick={() => setBackgroundWork((value) => !value)}
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-medium transition ${
                          backgroundWork
                            ? "border-foreground/20 bg-foreground text-background"
                            : "border-foreground/10 bg-foreground/[0.035] text-foreground/50"
                        }`}
                        title={backgroundWork
                          ? "Background mode: task continues after you leave the app"
                          : "Live mode: keep temporary browser previews in this session"}
                      >
                        {backgroundWork ? "Background" : "Live"}
                      </button>
                    )}
                  </div>
                  <Button
                    size="icon"
                    onClick={() => void sendMessage()}
                    disabled={!connected || sending || uploadingAttachment || (!draft.trim() && attachments.length === 0)}
                    className="size-8 rounded-full bg-foreground text-background shadow-sm transition-transform hover:scale-[1.03] hover:bg-foreground/90 active:scale-95 sm:size-9"
                    aria-label="Send"
                  >
                    {sending ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-[17px]" />}
                  </Button>
                </div>
              </div>
              <p className="mt-1.5 hidden text-center text-[10px] text-foreground/30 sm:block">
                {mode === "chat"
                  ? "AI inference is routed through verified RUNYI provider adapters."
                  : backgroundWork
                    ? "Background mode: this Work task can continue after you leave the app. Temporary browser images are not retained."
                    : "Live mode: best for interactive browser work and temporary screenshot previews. Switch to Background for long-running tasks."}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
